import React from 'react'
import { Field, Form, Formik, FormikErrors } from 'formik'
import { Box, Card, Columns, Element, Heading, Loader } from 'react-bulma-components'
import useStore from '../../store'
import { formatCompleteDate, formatISODate } from '../../utils/date'
import { RequestButton, RequestMessage } from '../request-components/request-components'
import {
  addDays,
  differenceInDays,
  isAfter,
  isBefore,
  isWithinInterval,
  nextSunday,
  parseISO,
} from 'date-fns'
import AutocompleteField from '../form/fields/autocomplete'
import SelectField from '../form/fields/select'
import ComponentFooter from '../sections/component-footer'
import { Organization } from '../../api/organizations'
import { useCreateMutation, useUpdateMutation } from '../../queries/missions'
import { buildListAsInputOptions } from '../../utils/forms'
import { Mission, MissionScheduleTemplate } from '../../api/missions'
import { AvailableWorker, User } from '../../api/users'
import { useUsersQuery } from '../../queries/users'
import { useJobTitlesQuery } from '../../queries/job-titles'
import FormWrapper from '../form/form-wrapper'
import { useFormWrapperPropsWithMutationData } from '../../hooks/use-form-wrapper-props-with-mutation-data'
import { WorkPeriodsEditor } from '../work-periods/work-periods-editor/work-periods-editor'
import { cloneDeep, isEqual, truncate } from 'lodash'
import { WorkPeriod } from '../../api/work-periods'
import {
  getWorkerAbsentLabelForOrganization,
  getUserLabel,
  workerIsIsAvailableForOrganizationAndDay,
  getWorkerAvailabilityForOrganization,
  getWorkerAbsentLabel,
  getWorkerAndOrganizationAssociation,
} from '../../utils/users'
import WorkerLabel from '../texts/worker-label'
import InputField from '../form/fields/input'
import RadioField from '../form/fields/radio'
import { WorkPeriodEditorFormErrors } from '../work-periods/work-periods-editor/work-period-editor'
import { validateWorkPeriodTheoreticalRange } from '../work-periods/validate-theoretical-range'
import { useServicesQuery } from '../../queries/services'
import WorkPeriodTags from '../work-periods/work-period-tags'
import { WorkPeriodTag } from '../../api/work-period-tags'
import { useOrganizationQuery } from '../../queries/organizations'
import { ScheduleTemplateEditor, createDefaultScheduleTemplate } from './schedule-templates-editor'

interface MissionFormProps {
  mission?: Mission
  readOnly?: boolean
}

export type ScheduleMode = 'fixed' | 'recurring'

export interface MissionFormValues {
  meta: {
    startDate: string // YYYY-mm-dd
    endDate: string // YYYY-mm-dd
    users: User[]
    resource?: Mission
  }
  /** Creation only: fixed dates (workPeriods) vs recurring (scheduleTemplate). */
  scheduleMode: ScheduleMode
  user: string
  employer: string
  address: string
  workPeriods: Array<
    {
      start?: { date: Date }
      end?: { date: Date }
    } & Partial<Omit<WorkPeriod, 'start' | 'end'>>
  >
  /** Creation only, used when scheduleMode === 'recurring'. */
  scheduleTemplate: MissionScheduleTemplate
  isValidated: boolean
  service: string
  jobTitle: string
  lastWeek: string
  tags: WorkPeriodTag[]
}

type MissionFormErrors = FormikErrors<MissionFormValues> &
  WorkPeriodEditorFormErrors & { scheduleTemplate?: string }

/**
 * Validates a single scheduleTemplate for recurring mode: start date required,
 * end > start if set, at least one week pattern, at least one day per pattern,
 * hours 0–24 and startHour < endHour.
 */
function validateScheduleTemplateForm(
  scheduleTemplate: MissionScheduleTemplate | undefined,
): string | undefined {
  if (!scheduleTemplate) return 'Renseignez le planning.'
  if (!scheduleTemplate.start) return 'Indiquez à partir de quelle date le planning s’applique.'
  if (scheduleTemplate.end && isAfter(scheduleTemplate.start, scheduleTemplate.end))
    return 'La date de fin doit être après la date de début.'
  if (!scheduleTemplate.weekPatterns?.length) return 'Indiquez au moins un jour travaillé.'
  for (let pi = 0; pi < scheduleTemplate.weekPatterns.length; pi++) {
    const p = scheduleTemplate.weekPatterns[pi]
    if (!p) continue
    const days = p.days ? Object.keys(p.days) : []
    if (days.length === 0) {
      return `Indiquez au moins un jour travaillé.`
    }
    for (const dayKey of days) {
      const slot = p.days[dayKey as unknown as 0 | 1 | 2 | 3 | 4 | 5 | 6]
      if (!slot) continue
      const { startHour, endHour } = slot
      const hasStart = typeof startHour === 'number'
      const hasEnd = typeof endHour === 'number'
      if (hasStart !== hasEnd) {
        return `Chaque jour travaillé doit avoir une heure de début et une heure de fin.`
      }
      if (!hasStart) continue
      const start = startHour as number
      const end = endHour as number
      if (start < 0 || start > 24 || end < 0 || end > 24)
        return 'Les heures doivent être comprises entre 0 h et 24 h.'
      // Allow overnight schedules (e.g. 22h–6h) where startHour > endHour.
      // Only reject when start and end are exactly equal (zero-length slot).
      if (start === end) return 'L’heure de début et l’heure de fin ne peuvent pas être identiques.'
    }
  }
  return undefined
}

const MissionForm: React.FunctionComponent<MissionFormProps> = ({ mission, readOnly = false }) => {
  const currentOrganization = useStore(state => state.session.currentOrganization) as Organization
  const organizationQuery = useOrganizationQuery(currentOrganization._id, currentOrganization.type)
  const employerId = mission?.employer?._id.toString() || currentOrganization._id.toString()

  //Is new mission or mission edition
  const isMissionEditForm = Boolean(mission)
  const isNewMissionForm = !isMissionEditForm

  const jobTitlesQuery = useJobTitlesQuery(
    {
      employer: employerId,
    },
    { enabled: Boolean(employerId) },
  )

  const servicesQuery = useServicesQuery()

  const usersQuery = useUsersQuery({
    'roles.kind': 'worker',
    filter: JSON.stringify({
      associations: {
        $elemMatch: {
          organization: currentOrganization._id,
          status: ['accepted', 'pending'],
        },
      },
    }),
  })

  const missionNotEditable = Boolean((mission && !mission.__actions.canBeUpdated) || readOnly)
  const missionEditable = !missionNotEditable
  const createMutation = useCreateMutation()
  const updateMutation = useUpdateMutation()
  const currentMutation = mission ? updateMutation : createMutation
  const formWrapperBaseProps = isNewMissionForm
    ? formWrapperBaseCreateProps
    : formWrapperBaseUpdateProps
  const formWrapperProps = useFormWrapperPropsWithMutationData(
    formWrapperBaseProps,
    currentMutation,
  )
  let usersList: User[]
  let servicesList: Mission['service'][]

  if (missionNotEditable) {
    usersList = mission?.user ? [mission.user as User] : []
    servicesList = mission?.service ? [mission.service] : []
  } else {
    usersList = usersQuery?.data || []
    servicesList = servicesQuery?.data || []
  }

  // start and end dates stored as ISO dates YYYY-mm-dd
  // we always display at least 3 days
  const now = new Date()
  const today = now
  const todayISO = formatISODate(now)
  const sunday = nextSunday(now)
  const end = differenceInDays(sunday, today) <= 3 ? addDays(sunday, 7) : sunday
  const endISO = formatISODate(end)

  const startDateISO = mission?.start ? formatISODate(new Date(mission.start)) : todayISO
  const lastWorkPeriodStartDate = mission?.workPeriods[mission.workPeriods.length - 1]?.start.date
  const endDateISO = lastWorkPeriodStartDate
    ? formatISODate(new Date(lastWorkPeriodStartDate))
    : endISO

  if (!servicesQuery.isFetched || !jobTitlesQuery.isFetched || !usersQuery.isFetched)
    return <Loader />

  return (
    <FormWrapper {...formWrapperProps}>
      <Formik<MissionFormValues>
        initialValues={{
          meta: {
            startDate: startDateISO,
            endDate: endDateISO,
            users: usersList,
            resource: mission,
          },
          scheduleMode: !mission
            ? 'recurring'
            : mission?.scheduleTemplate?.weekPatterns?.length
              ? 'recurring'
              : ('fixed' as ScheduleMode),
          user: mission?.user?._id || '',
          employer: mission?.employer?._id.toString() || currentOrganization._id.toString(),
          address: mission?.address ?? '',
          workPeriods:
            mission?.workPeriods.map(wP => ({
              ...wP,
              start: { date: new Date(wP.start.date) },
              end: { date: new Date(wP.end.date) },
            })) ?? [],
          scheduleTemplate: mission?.scheduleTemplate
            ? {
                start: new Date(mission.scheduleTemplate.start),
                end: mission.scheduleTemplate.end ? new Date(mission.scheduleTemplate.end) : null,
                weekPatterns: mission.scheduleTemplate.weekPatterns.map(p => ({
                  days: { ...p.days },
                })),
              }
            : createDefaultScheduleTemplate(),
          isValidated: false,
          service: mission?.service?._id || '',
          jobTitle: (mission?.jobTitle?._id as string) || '',
          lastWeek: '',
          tags: mission?.tags || [],
        }}
        enableReinitialize
        validate={values => {
          const errors: MissionFormErrors = {}

          if (!values.user) errors['user'] = 'Champ requis'
          if (!values.service) errors['service'] = 'Champ requis'
          if (!values.employer) errors['employer'] = 'Champ requis'
          if (!values.jobTitle) errors['jobTitle'] = 'Champ requis'
          if (!values.address) errors.address = 'Champ requis'

          const scheduleMode = values.scheduleMode ?? 'fixed'
          const isRecurring = scheduleMode === 'recurring'

          if (isRecurring) {
            const scheduleErr = validateScheduleTemplateForm(values.scheduleTemplate)
            if (scheduleErr) errors['scheduleTemplate'] = scheduleErr
          } else {
            // Mode dates fixes: require workPeriods and meta dates
            if (!values.workPeriods || values.workPeriods.length === 0)
              errors['workPeriods'] = "Pas d'horaire de mission"
          }

          const startDate = parseISO(values.meta.startDate)
          const endDate = parseISO(values.meta.endDate)
          const worker = usersQuery.data?.find(u => u._id === values.user)

          // Dates and workPeriods validation only for fixed mode
          if (!isRecurring) {
            if (isAfter(startDate, endDate)) {
              if (errors.meta === undefined) errors.meta = {}
              errors.meta['startDate'] = 'La date de début doit être avant la date de fin'
            }
            if (Math.abs(differenceInDays(startDate, endDate)) > 365) {
              if (errors.meta === undefined) errors.meta = {}
              errors.meta['endDate'] = 'Une mission doit durer maximum 1 an'
            }
          }

          if (!isRecurring && values.workPeriods?.length) {
            for (let index = 0; index <= values.workPeriods.length; index++) {
              const workPeriod = values.workPeriods[index]
              const nextWorkPeriod = values.workPeriods[index + 1]
              const firstWorkPeriodStartDate =
                workPeriod && workPeriod.start ? workPeriod.start.date : undefined
              const firstWorkPeriodEndDate =
                workPeriod && workPeriod.end ? workPeriod.end.date : undefined
              const nextWorkPeriodStartDate =
                nextWorkPeriod && nextWorkPeriod.start ? nextWorkPeriod.start.date : undefined

              // If the end of a workPeriod overlaps the start of the next workPeriod
              if (firstWorkPeriodStartDate && firstWorkPeriodEndDate && nextWorkPeriodStartDate) {
                if (
                  isBefore(nextWorkPeriodStartDate, firstWorkPeriodEndDate) ||
                  isEqual(firstWorkPeriodEndDate, nextWorkPeriodStartDate)
                ) {
                  const niceDate = formatCompleteDate(firstWorkPeriodStartDate)
                  const isoDate = formatISODate(firstWorkPeriodStartDate)
                  const error = `${niceDate} : L'heure de fin ne peut être plus grande ou égale à l'heure du début du jour suivant`
                  errors['workPeriods'] = error
                  ;(errors[`workPeriod-${isoDate}`] ??= []).push(error)
                }
              }

              // If a workPeriod has no start date set
              if (firstWorkPeriodStartDate === undefined && firstWorkPeriodEndDate) {
                const date = firstWorkPeriodStartDate ?? firstWorkPeriodEndDate
                const niceDate = formatCompleteDate(date)
                const isoDate = formatISODate(date)
                const error = `${niceDate} : L'heure de début n'est pas précisée`
                errors['workPeriods'] = error
                ;(errors[`workPeriod-${isoDate}`] ??= []).push(error)
              }
              // If a workPeriod has no end date set
              if (firstWorkPeriodStartDate && firstWorkPeriodEndDate === undefined) {
                const date = firstWorkPeriodStartDate ?? firstWorkPeriodEndDate
                const niceDate = formatCompleteDate(date)
                const isoDate = formatISODate(date!)
                const error = `${niceDate} : L'heure de fin n'est pas précisée`
                errors['workPeriods'] = error
                ;(errors[`workPeriod-${isoDate}`] ??= []).push(error)
              }

              // Theoretical range: end after start (date-aware), duration <= 24h
              const rangeErrors =
                workPeriod &&
                validateWorkPeriodTheoreticalRange(workPeriod?.start?.date, workPeriod?.end?.date)
              if (rangeErrors && (rangeErrors.startDate || rangeErrors.endDate)) {
                const date = firstWorkPeriodStartDate ?? firstWorkPeriodEndDate
                const niceDate = date ? formatCompleteDate(date) : ''
                const isoDate = date ? formatISODate(date) : ''
                const error = `${niceDate} : ${rangeErrors.startDate ?? rangeErrors.endDate}`
                errors['workPeriods'] = error
                if (isoDate) (errors[`workPeriod-${isoDate}`] ??= []).push(error)
              }

              const workerIsAvailable =
                workPeriod?.start?.date &&
                workPeriod?.end?.date &&
                worker &&
                workerIsIsAvailableForOrganizationAndDay(
                  new Date(workPeriod.start.date),
                  worker,
                  currentOrganization,
                )

              if (
                !mission &&
                firstWorkPeriodStartDate &&
                worker &&
                workPeriod?.start?.date &&
                workPeriod?.end?.date &&
                !workerIsAvailable
              ) {
                errors['workPeriods'] = getWorkerAbsentLabelForOrganization(
                  worker,
                  currentOrganization,
                )
                const isoDate = formatISODate(firstWorkPeriodStartDate)
                if (firstWorkPeriodStartDate)
                  (errors[`workPeriod-${isoDate}`] ??= []).push(
                    getWorkerAbsentLabelForOrganization(worker, currentOrganization),
                  )
              }
            }
          }

          return errors
        }}
        onSubmit={async values => {
          if (missionNotEditable) return

          if (mission) {
            const { service, address } = values
            const { startDate, endDate } = values.meta
            const missionStartDate = parseISO(startDate)
            const missionEndDate = addDays(parseISO(endDate), 1)
            const workPeriods = cloneDeep(
              values.workPeriods as Array<Required<MissionFormValues['workPeriods'][0]>>,
            )
              .filter(wP =>
                isWithinInterval(wP.start.date, { start: missionStartDate, end: missionEndDate }),
              )
              .map(wP => ({ start: { date: wP.start.date }, end: { date: wP.end.date } }))

            updateMutation.mutate({
              _id: mission._id,
              service,
              address,
              workPeriods,
            })
            return
          }

          // Create: send either workPeriods (fixed) or scheduleTemplate (recurring)
          const { service, jobTitle, user, address, tags } = values
          const isRecurring = values.scheduleMode === 'recurring'

          if (isRecurring) {
            createMutation.mutate({
              user,
              jobTitle,
              service,
              address,
              tags: tags?.map(tag => tag._id),
              scheduleTemplate: values.scheduleTemplate,
            })
          } else {
            const { startDate, endDate } = values.meta
            const missionStartDate = parseISO(startDate)
            const missionEndDate = addDays(parseISO(endDate), 1)
            const workPeriods = cloneDeep(
              values.workPeriods as Array<Required<MissionFormValues['workPeriods'][0]>>,
            )
              .filter(wP =>
                isWithinInterval(wP.start.date, { start: missionStartDate, end: missionEndDate }),
              )
              .map(wP => ({ start: { date: wP.start.date }, end: { date: wP.end.date } }))

            createMutation.mutate({
              user,
              jobTitle,
              service,
              workPeriods,
              address,
              tags: tags?.map(tag => tag._id),
            })
          }
        }}
      >
        {props => {
          const selectedUser = usersList.find(u => u._id.toString() === props.values.user)
          const selectedJobTitle = jobTitlesQuery.data?.find(
            jT => props.values.jobTitle.toString() === jT._id.toString(),
          )

          return (
            <Form>
              <Element mb={6} mt={5}>
                <Heading renderAs="h2" size={4}>
                  Informations du <WorkerLabel />
                </Heading>
                <Columns>
                  {!mission && (
                    <Columns.Column>
                      <Field
                        label={<WorkerLabel />}
                        name="user"
                        component={AutocompleteField}
                        required
                        fullwidth
                        onlySelect
                        items={buildListAsInputOptions(usersList, {
                          labelBuilder: (user: User) => getUserLabel(user),
                          renderLabel: user =>
                            UserInputLabel({ user, organization: currentOrganization }),
                        })}
                        selectDefaultChooseOptionLabel="Choisir l'intérimaire"
                        disabled={missionNotEditable || isMissionEditForm}
                      />
                      {props.values.user && selectedUser && (
                        <Card>
                          <Card.Header>
                            <Card.Header.Title>Travailleur sélectionné</Card.Header.Title>
                          </Card.Header>
                          <Card.Content>
                            <UserInputLabel
                              user={selectedUser}
                              organization={currentOrganization}
                            />
                          </Card.Content>
                        </Card>
                      )}
                    </Columns.Column>
                  )}
                  <Columns.Column>
                    <Field
                      label="Métier"
                      name="jobTitle"
                      component={SelectField}
                      required
                      fullwidth
                      options={buildListAsInputOptions(jobTitlesQuery.data || [])}
                      selectDefaultChooseOptionLabel={
                        props.values.employer
                          ? 'Choisir le Métier'
                          : "Sélectionnez d'abord un Employeur"
                      }
                      disabled={missionNotEditable || !props.values.employer || isMissionEditForm}
                    />
                    {props.values.jobTitle && selectedJobTitle?.complementaryInformations ? (
                      <Card>
                        <Card.Header>
                          <Card.Header.Title>Ces infos seront transmises</Card.Header.Title>
                        </Card.Header>
                        <Card.Content>{selectedJobTitle?.complementaryInformations}</Card.Content>
                      </Card>
                    ) : undefined}
                  </Columns.Column>
                  <Columns.Column>
                    <Field
                      label="Affectation"
                      name="service"
                      component={AutocompleteField}
                      required
                      items={buildListAsInputOptions(servicesList)}
                      disabled={missionNotEditable || !props.values.employer}
                      onlySelect
                      multiple={false}
                    />
                  </Columns.Column>
                </Columns>
              </Element>
              <Element mt={5} mb={5}>
                <Heading renderAs="h2" size={4}>
                  Catégories
                </Heading>

                <WorkPeriodTags
                  tags={props.values.tags || []}
                  formProps={{
                    onSubmit: params => {
                      props.setFieldValue('tags', params.tags)
                    },
                    onCancel: () => {},
                    dateRange: false,
                  }}
                  onTagDelete={tagId => {
                    props.setFieldValue(
                      'tags',
                      props.values.tags?.filter(tag => tag._id !== tagId),
                    )
                  }}
                  isEditable={isNewMissionForm}
                />
              </Element>
              <Element mt={5} mb={5}>
                <Heading renderAs="h2" size={4}>
                  Horaire
                </Heading>
                {isNewMissionForm && (
                  <Box>
                    <Element mb={5}>
                      <Field
                        name="scheduleMode"
                        label="Type d'horaire"
                        component={RadioField}
                        options={[
                          {
                            value: 'recurring',
                            label: 'Répéter un masque semaine par semaine (récurrence automatique)',
                          },
                          {
                            value: 'fixed',
                            label: 'Saisir les horaires manuellement jour par jour (maximum 1 an)',
                          },
                        ]}
                        disabled={missionNotEditable}
                      />
                    </Element>
                    <Element renderAs="hr" mb={5} />
                    {props.values.scheduleMode === 'recurring' ? (
                      <ScheduleTemplateEditor
                        scheduleTemplate={props.values.scheduleTemplate}
                        setScheduleTemplate={v => props.setFieldValue('scheduleTemplate', v)}
                        errors={
                          typeof props.errors.scheduleTemplate === 'string'
                            ? props.errors.scheduleTemplate
                            : undefined
                        }
                        disabled={missionNotEditable}
                        breakRule={selectedJobTitle?.clockingRule?.break}
                        userId={props.values.user}
                      />
                    ) : (
                      <WorkPeriodsEditor
                        workPeriods={props.values.workPeriods}
                        jobTitle={props.values.jobTitle}
                        initialWorkPeriods={props.initialValues.workPeriods}
                        resource={mission}
                        user={props.values.user}
                        setWorkPeriods={wPs => props.setFieldValue('workPeriods', wPs)}
                        errors={props.errors}
                        startDate={props.values.meta.startDate}
                        endDate={props.values.meta.endDate}
                      />
                    )}
                  </Box>
                )}
                {isMissionEditForm && (
                  <>
                    {mission?.scheduleTemplate?.start ? (
                      <Box className="mt-5">
                        <Heading size={5} renderAs="h3" className="mb-3">
                          Semaine(s) type (lecture seule)
                        </Heading>
                        <ScheduleTemplateEditor
                          scheduleTemplate={props.values.scheduleTemplate}
                          setScheduleTemplate={() => {}}
                          disabled
                          breakRule={selectedJobTitle?.clockingRule?.break}
                          userId={props.values.user}
                        />
                      </Box>
                    ) : (
                      <WorkPeriodsEditor
                        workPeriods={props.values.workPeriods}
                        jobTitle={props.values.jobTitle}
                        initialWorkPeriods={props.initialValues.workPeriods}
                        resource={mission}
                        user={props.values.user}
                        setWorkPeriods={wPs => props.setFieldValue('workPeriods', wPs)}
                        errors={props.errors}
                        startDate={props.values.meta.startDate}
                        endDate={props.values.meta.endDate}
                      />
                    )}
                  </>
                )}
              </Element>
              <Element mt={5} mb={5}>
                <Field
                  name="address"
                  label="Adresse de la prestation"
                  component={AutocompleteField}
                  required
                  items={buildListAsInputOptions(organizationQuery.data?.addresses ?? [])}
                  disabled={missionNotEditable || !props.values.employer}
                />
              </Element>
              {mission?.complementaryInformations ? (
                <>
                  <Element mt={5} mb={5}>
                    <Heading renderAs="h2" size={4}>
                      Informations complémentaires
                    </Heading>
                    <Field
                      name="complementaryInformations"
                      component={InputField}
                      disabled={mission}
                    />
                  </Element>
                </>
              ) : undefined}
              <ComponentFooter>
                <ComponentFooter.Left>
                  {(isNewMissionForm || missionEditable) && (
                    <>
                      <RequestButton
                        type="submit"
                        color="primary"
                        title="submit"
                        data-test="submit-mission-form"
                        mutation={currentMutation}
                      >
                        {mission
                          ? 'Mettre à jour les données de la Mission'
                          : 'Créer une Nouvelle Mission'}
                      </RequestButton>
                      <RequestMessage mutation={currentMutation} />
                    </>
                  )}
                </ComponentFooter.Left>
                <ComponentFooter.Right>
                  <></>
                </ComponentFooter.Right>
              </ComponentFooter>
            </Form>
          )
        }}
      </Formik>
    </FormWrapper>
  )
}

export default MissionForm

const formWrapperBaseCreateProps = {
  loadingProps: { body: 'Création de la Mission...' },
  successProps: {
    title: 'Mission créée',
    body: {
      actions: [
        {
          label: 'Liste des Missions',
          to: '/missions',
        },
        {
          label: 'Créer une Nouvelle Mission Interne',
          to: '/missions',
        },
      ],
    },
  },
}
const formWrapperBaseUpdateProps = {
  loadingProps: { body: 'Mise à jour de la Mission...' },
  successProps: {
    title: 'Mission mise à jour',
    body: {
      actions: [
        {
          label: 'Liste des Missions',
          to: '/missions',
        },
        {
          label: 'Voir la Mission',
        },
      ],
    },
  },
}

export const UserInputLabel = ({
  user,
  organization,
}: {
  user: User | AvailableWorker
  organization: Organization
}): React.ReactNode => {
  const userLabel = getUserLabel(user)
  const availability =
    'availability' in user
      ? user.availability
      : getWorkerAvailabilityForOrganization(user, organization)
  const internalComment =
    'availability' in user
      ? user.internalComment
      : getWorkerAndOrganizationAssociation(user, organization)?.internalComment
  const internalInformation =
    'availability' in user
      ? user.internalInformation
      : getWorkerAndOrganizationAssociation(user, organization)?.internalInformation
  const absentLabel =
    availability &&
    (!availability.absentTo || isAfter(new Date(availability.absentTo), new Date())) &&
    getWorkerAbsentLabel(availability, user, true)
  return (
    <>
      {userLabel}
      {absentLabel && (
        <>
          <br />
          <Element renderAs="small">
            <b>Absent:</b> {absentLabel.toLowerCase()}
          </Element>
        </>
      )}

      {internalComment && (
        <Element>
          <Element renderAs="small">
            <b>Infos:</b> {truncate(internalComment, { length: 70 })}
          </Element>
        </Element>
      )}
      {internalInformation && (
        <Element>
          <Element renderAs="small">
            <b>Commentaires:</b> {truncate(internalInformation, { length: 70 })}
          </Element>
        </Element>
      )}
    </>
  )
}
